import React, { useState, useEffect, useRef } from 'react';
import { Download, Search, MapPin, Plus, Trash2, ArrowLeft, Store, Globe, ShieldAlert, Ban, CheckCircle } from 'lucide-react';
import { collection, getDocs, addDoc, deleteDoc, doc, onSnapshot, query, orderBy, limit, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { flagEmoji, countryName } from '../utils/country';
import { theme } from '../themes';
import { addBasemap } from '../utils/basemap';

const AdminDashboard = ({ db, appId, onExit }) => {
  const [venues, setVenues] = useState([]);
  const [loadingExport, setLoadingExport] = useState(false);
  const [world, setWorld] = useState(null);        // { total, cells, countries }
  const [loadingWorld, setLoadingWorld] = useState(false);
  const worldMapRef = useRef(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [savingVenue, setSavingVenue] = useState(false);

  // Form State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null); // {lat, lng}
  const [venueName, setVenueName] = useState('');
  const [venueType, setVenueType] = useState('classic');
  const [venueDeal, setVenueDeal] = useState('');

  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // --- 1. FETCH EXISTING VENUES ---
  // ---- MODERATION QUEUE (guideline 1.2: act on reports within 24h) ----
  // Live feeds of user reports + block notifications, newest first.
  const [reports, setReports] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [modBusy, setModBusy] = useState(null); // report id being acted on
  useEffect(() => {
    const repQ = query(collection(db, 'artifacts', appId, 'public', 'data', 'reports'), orderBy('createdAt', 'desc'), limit(100));
    const blkQ = query(collection(db, 'artifacts', appId, 'public', 'data', 'blocks'), orderBy('createdAt', 'desc'), limit(100));
    const u1 = onSnapshot(repQ, (s) => setReports(s.docs.map(d => ({ id: d.id, ...d.data() }))),
      (e) => console.warn('reports feed failed', e));
    const u2 = onSnapshot(blkQ, (s) => setBlocks(s.docs.map(d => ({ id: d.id, ...d.data() }))),
      (e) => console.warn('blocks feed failed', e));
    return () => { u1(); u2(); };
  }, [db, appId]);

  // Remove the offending message from the squad's chat (global takedown).
  const removeContent = async (rep) => {
    if (!rep.squadId || !rep.messageId) return alert('No message reference on this report.');
    if (!window.confirm('Remove this message for everyone?')) return;
    setModBusy(rep.id);
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', rep.squadId, 'messages', rep.messageId));
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', rep.id),
        { resolved: true, action: 'content_removed', resolvedAt: serverTimestamp() });
    } catch (e) { console.error(e); alert('Remove failed: ' + (e.code || e.message)); }
    setModBusy(null);
  };

  // Eject the offender: banned/{uid} strips message + photo-upload rights at
  // the rules layer. Also removes the reported message if it still exists.
  const ejectUser = async (rep) => {
    const uid = rep.blockedUid || rep.senderId;
    if (!uid) return;
    if (!window.confirm(`Eject ${rep.senderName || rep.blockedName || uid}? They will no longer be able to post.`)) return;
    setModBusy(rep.id);
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'banned', uid), {
        name: rep.senderName || rep.blockedName || null,
        reason: rep.type || 'blocked',
        reportId: rep.id,
        bannedAt: serverTimestamp(),
      });
      if (rep.squadId && rep.messageId) {
        try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', rep.squadId, 'messages', rep.messageId)); } catch { /* already gone */ }
      }
      if (rep.reportedBy) { // it's a report doc → mark resolved
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', rep.id),
          { resolved: true, action: 'user_ejected', resolvedAt: serverTimestamp() });
      }
    } catch (e) { console.error(e); alert('Eject failed: ' + (e.code || e.message)); }
    setModBusy(null);
  };

  const dismissReport = async (rep) => {
    setModBusy(rep.id);
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', rep.id),
        { resolved: true, action: 'dismissed', resolvedAt: serverTimestamp() });
    } catch (e) { console.error(e); }
    setModBusy(null);
  };

  const openReports = reports.filter(r => !r.resolved);

  useEffect(() => {
    const venuesRef = collection(db, 'artifacts', appId, 'public', 'data', 'venues');
    const unsub = onSnapshot(venuesRef, (snap) => {
      const vList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVenues(vList);
    });
    return () => unsub();
  }, [db, appId]);

  // --- 2. INITIALIZE MINI-MAP & CLICK LISTENER ---
  useEffect(() => {
    const container = document.getElementById('admin-map-container');
    if (!container || !window.L) return;

    if (!mapRef.current) {
      const L = window.L;
      // Default to a zoomed-out view of North America for global use
      const map = L.map('admin-map-container', { zoomControl: false }).setView([39.8283, -98.5795], 4);
      addBasemap(map, theme.map);
      mapRef.current = map;

      // NEW: Allow clicking the map to manually set the coordinate
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        setSelectedLocation({ lat, lng });

        if (markerRef.current) markerRef.current.remove();
        
        const icon = L.divIcon({
          className: 'bg-transparent',
          html: `<div class="w-4 h-4 rounded-full border-2 border-white animate-pulse" style="background:var(--accent-1);box-shadow:0 0 15px var(--pin-glow)"></div>`,
          iconSize: [16, 16]
        });
        markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
      });
    }
  }, []);

  // --- 3. HANDLE ADDRESS SEARCH (OpenStreetMap Nominatim) ---
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setLoadingSearch(true);
    try {
      // Free geocoding API - Removed hardcoded Scottsdale constraint
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`);
      const data = await res.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        setSelectedLocation({ lat, lng });

        // Update Map
        const L = window.L;
        const map = mapRef.current;
        map.flyTo([lat, lng], 17);

        if (markerRef.current) markerRef.current.remove();
        
        const icon = L.divIcon({
          className: 'bg-transparent',
          html: `<div class="w-4 h-4 rounded-full border-2 border-white animate-pulse" style="background:var(--accent-1);box-shadow:0 0 15px var(--pin-glow)"></div>`,
          iconSize: [16, 16]
        });
        markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
        
        // Auto-fill name if empty
        if (!venueName) setVenueName(data[0].name || searchQuery);
      } else {
        alert("Exact address not found. Try searching just the city (e.g., 'Chicago'), then click on the map to drop the pin manually!");
      }
    } catch (err) {
      console.error("Geocoding Error:", err);
      alert("Failed to search address.");
    }
    setLoadingSearch(false);
  };

  // --- 4. SAVE NEW VENUE ---
  const handleAddVenue = async () => {
    if (!selectedLocation || !venueName) {
      alert("Please search for a location and provide a name.");
      return;
    }
    setSavingVenue(true);
    try {
      const venuesRef = collection(db, 'artifacts', appId, 'public', 'data', 'venues');
      await addDoc(venuesRef, {
        name: venueName,
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        type: venueType,
        deal: venueDeal || null
      });

      // Reset Form
      setSearchQuery('');
      setVenueName('');
      setVenueDeal('');
      setVenueType('classic');
      setSelectedLocation(null);
      if (markerRef.current) markerRef.current.remove();

      alert("Venue Added Successfully!");
    } catch (err) {
      console.error("Save Error:", err);
      alert("Failed to add venue.");
    }
    setSavingVenue(false);
  };

  // --- 5. DELETE VENUE ---
  const handleDeleteVenue = async (id) => {
    if (!window.confirm("Are you sure you want to delete this venue?")) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'venues', id));
    } catch (err) {
      console.error("Delete Error:", err);
    }
  };

  // --- 5b. WORLD MAP (anonymous session aggregate) ---
  // Reads the admin-only `sessions` collection: coarse geohash cells + totals,
  // no identities. Loaded on demand so opening HQ doesn't read thousands of docs.
  const loadWorld = async () => {
    setLoadingWorld(true);
    try {
      const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'sessions'), orderBy('createdAt', 'desc'), limit(5000));
      const snap = await getDocs(q);
      const cells = new Map(), countries = new Map();
      let total = 0;
      snap.forEach(d => {
        const s = d.data(); total++;
        if (s.geohash && Number.isFinite(s.lat) && Number.isFinite(s.lng)) {
          const c = cells.get(s.geohash) || { lat: s.lat, lng: s.lng, n: 0, drinks: 0 };
          c.n++; c.drinks += s.drinks || 0; cells.set(s.geohash, c);
        }
        const cc = s.countryCode || '??';
        const k = countries.get(cc) || { cc, n: 0, drinks: 0, distance: 0 };
        k.n++; k.drinks += s.drinks || 0; k.distance += s.distance || 0; countries.set(cc, k);
      });
      setWorld({
        total,
        cells: [...cells.values()],
        countries: [...countries.values()].sort((a, b) => b.n - a.n),
      });
    } catch (e) {
      console.error('World map load failed:', e);
      alert('Could not load session data. Check admin permissions.');
    }
    setLoadingWorld(false);
  };

  useEffect(() => {
    if (!world || !window.L) return;
    const container = document.getElementById('admin-world-map');
    if (!container) return;
    const L = window.L;
    if (worldMapRef.current) { worldMapRef.current.remove(); worldMapRef.current = null; }
    const map = L.map('admin-world-map', { zoomControl: true, attributionControl: false }).setView([25, 0], 2);
    addBasemap(map, theme.map);
    const max = Math.max(1, ...world.cells.map(c => c.n));
    world.cells.forEach(c => {
      const r = 4 + 10 * Math.sqrt(c.n / max);
      L.circleMarker([c.lat, c.lng], { radius: r, color: '#e683a9', weight: 1, fillColor: '#e683a9', fillOpacity: 0.55 })
        .bindPopup(`<b>${c.n} night${c.n === 1 ? '' : 's'}</b><br>${c.drinks} drinks<br><small>~5 km cell</small>`)
        .addTo(map);
    });
    worldMapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { if (worldMapRef.current) { worldMapRef.current.remove(); worldMapRef.current = null; } };
  }, [world]);

  // --- 6. EXPORT LEADS ---
  const handleExportLeads = async () => {
    setLoadingExport(true);
    try {
        const querySnapshot = await getDocs(collection(db, "marketing_leads"));
        let csvContent = "data:text/csv;charset=utf-8,Email,Username,Source,CapturedAt\n";
        
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            const date = data.capturedAt ? new Date(data.capturedAt.seconds * 1000).toLocaleDateString() : 'N/A';
            csvContent += `${data.email},${data.username},${data.source},${date}\n`;
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `bender_leads_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert(`Exported ${querySnapshot.size} leads successfully!`);
    } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Check permissions.");
    }
    setLoadingExport(false);
  };

  return (
    <div className="absolute inset-0 z-[9999] bg-black text-white overflow-y-auto font-sans p-4 pt-12 pb-20">
      
      <div className="max-w-2xl mx-auto space-y-6">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
          <div>
            <h1 className="text-3xl font-black italic text-brand-teal tracking-tighter">HQ DASHBOARD</h1>
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Authorized Access Only</p>
          </div>
          <button onClick={onExit} className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase flex items-center gap-2">
            <ArrowLeft size={16} /> Exit
          </button>
        </div>

        {/* MODERATION QUEUE — first card on purpose: reports carry a 24h SLA */}
        <div className={`bg-zinc-900 border p-6 rounded-2xl space-y-4 ${openReports.length ? 'border-red-500/60' : 'border-white/10'}`}>
          <div className="flex items-center gap-2">
            <ShieldAlert className={openReports.length ? 'text-red-400' : 'text-brand-teal'} size={20} />
            <h2 className="text-lg font-black uppercase tracking-tight">Moderation</h2>
            {openReports.length > 0 && (
              <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{openReports.length} open</span>
            )}
          </div>
          <p className="text-zinc-500 text-xs font-bold">User reports and block notifications. Policy: act within 24 hours — remove the content, eject repeat/serious offenders.</p>

          {reports.length === 0 && blocks.length === 0 && (
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600 text-center py-3">No reports · all quiet</p>
          )}

          {reports.map((r) => (
            <div key={r.id} className={`border rounded-xl p-4 space-y-3 ${r.resolved ? 'border-zinc-800 bg-black/30 opacity-60' : 'border-red-900/60 bg-black/50'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${r.type === 'chat_photo' ? 'bg-purple-900/60 text-purple-300' : 'bg-blue-900/60 text-blue-300'}`}>
                      {r.type === 'chat_photo' ? 'Photo' : 'Text'}
                    </span>
                    <span className="text-xs font-bold text-white truncate">{r.senderName || r.senderId}</span>
                    <span className="text-[9px] font-bold text-zinc-500">squad {r.squadId}</span>
                    {r.resolved && (
                      <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400">{r.action || 'resolved'}</span>
                    )}
                  </div>
                  {r.text && r.text !== '📷 Photo' && (
                    <p className="text-xs font-bold text-zinc-300 mt-1.5 break-words">“{r.text}”</p>
                  )}
                  <p className="text-[9px] font-bold text-zinc-600 mt-1">
                    {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleString() : '—'} · reported by {r.reportedBy?.slice(0, 8)}…
                  </p>
                </div>
                {r.imageUrl && (
                  <a href={r.imageUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <img src={r.imageUrl} alt="Reported content" className="w-16 h-16 object-cover rounded-lg border border-zinc-700" />
                  </a>
                )}
              </div>
              {!r.resolved && (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => removeContent(r)} disabled={modBusy === r.id}
                    className="flex items-center gap-1.5 bg-red-900/40 border border-red-800 text-red-300 font-black text-[9px] uppercase tracking-widest px-3 py-2 rounded-lg disabled:opacity-50">
                    <Trash2 size={12} /> Remove content
                  </button>
                  <button onClick={() => ejectUser(r)} disabled={modBusy === r.id}
                    className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-600 text-white font-black text-[9px] uppercase tracking-widest px-3 py-2 rounded-lg disabled:opacity-50">
                    <Ban size={12} /> Eject user
                  </button>
                  <button onClick={() => dismissReport(r)} disabled={modBusy === r.id}
                    className="flex items-center gap-1.5 text-zinc-500 font-black text-[9px] uppercase tracking-widest px-3 py-2 rounded-lg disabled:opacity-50">
                    <CheckCircle size={12} /> Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}

          {blocks.length > 0 && (
            <div className="pt-2 border-t border-zinc-800">
              <p className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mb-2">Block notifications</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto no-scrollbar">
                {blocks.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2 bg-black/40 border border-zinc-800 rounded-lg px-3 py-2">
                    <div className="min-w-0 text-[10px] font-bold text-zinc-400 truncate">
                      <span className="text-white">{b.blockedName || b.blockedUid?.slice(0, 8)}</span> blocked in squad {b.squadId}
                      {b.text && b.text !== '📷 Photo' ? <span className="text-zinc-500"> · “{b.text}”</span> : null}
                    </div>
                    <button onClick={() => ejectUser(b)}
                      className="shrink-0 text-[9px] font-black uppercase tracking-widest text-red-400 border border-red-900 px-2 py-1 rounded">
                      Eject
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* EXPORT LEADS CARD */}
        <div className="bg-zinc-900 border border-white/10 p-6 rounded-2xl flex justify-between items-center">
          <div>
            <h2 className="text-lg font-black uppercase tracking-tight">Marketing Leads</h2>
            <p className="text-zinc-500 text-xs font-bold mt-1">Download all registered users from EmailGate.</p>
          </div>
          <button 
            onClick={handleExportLeads}
            disabled={loadingExport}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-black px-6 py-3 rounded-xl uppercase text-sm flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {loadingExport ? "Processing..." : <><Download size={18} /> Export CSV</>}
          </button>
        </div>

        {/* WORLD MAP CARD — where the nights happen (anonymous aggregate) */}
        <div className="bg-zinc-900 border border-white/10 p-6 rounded-2xl space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Globe className="text-brand-teal" size={20} />
              <h2 className="text-lg font-black uppercase tracking-tight">Where the nights happen</h2>
            </div>
            <button onClick={loadWorld} disabled={loadingWorld} className="bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded-xl font-bold text-xs uppercase disabled:opacity-50">
              {loadingWorld ? 'Loading…' : world ? 'Refresh' : 'Load map'}
            </button>
          </div>
          <p className="text-zinc-500 text-xs font-bold">Anonymous, ~5 km cells. No names, no accounts — just where sessions ended. Last 5,000 sessions.</p>

          {world && (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-black/50 border border-zinc-700 rounded-xl p-3">
                  <div className="text-2xl font-black text-white italic">{world.total.toLocaleString()}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Nights</div>
                </div>
                <div className="bg-black/50 border border-zinc-700 rounded-xl p-3">
                  <div className="text-2xl font-black text-white italic">{world.countries.filter(c => c.cc !== '??').length}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Countries</div>
                </div>
                <div className="bg-black/50 border border-zinc-700 rounded-xl p-3">
                  <div className="text-2xl font-black text-white italic">{world.cells.length}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Cities / cells</div>
                </div>
              </div>
              <div className="w-full h-[320px] bg-black rounded-xl border border-zinc-700 overflow-hidden">
                <div id="admin-world-map" className="w-full h-full z-0"></div>
              </div>
              <div className="space-y-1.5">
                {world.countries.slice(0, 15).map(c => (
                  <div key={c.cc} className="flex items-center justify-between p-2.5 border border-zinc-800 rounded-xl bg-black/40">
                    <div className="flex items-center gap-2 text-sm font-bold text-white">
                      <span className="text-lg">{c.cc === '??' ? '🏳️' : flagEmoji(c.cc)}</span>
                      {c.cc === '??' ? 'Unknown (pre-build-5)' : countryName(c.cc)}
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                      {c.n} nights • {c.drinks} 🍺 • {c.n ? (c.drinks / c.n).toFixed(1) : 0}/night
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ADD VENUE CARD */}
        <div className="bg-zinc-900 border border-white/10 p-6 rounded-2xl space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Store className="text-brand-pink" size={20} />
            <h2 className="text-lg font-black uppercase tracking-tight">Add New Bar / Venue</h2>
          </div>

          {/* Search Row */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <input 
                type="text" 
                placeholder="Search Address or Bar Name..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-black border border-zinc-700 rounded-xl py-3 pl-10 pr-4 text-sm font-bold focus:outline-none focus:border-brand-teal text-white placeholder:text-zinc-600"
              />
              <Search className="absolute left-3 top-3 text-zinc-500" size={18} />
            </div>
            <button type="submit" disabled={loadingSearch} className="bg-zinc-800 hover:bg-zinc-700 px-6 rounded-xl font-bold text-sm uppercase disabled:opacity-50">
              {loadingSearch ? "Searching..." : "Find"}
            </button>
          </form>

          {/* Map Preview */}
          <div className="w-full h-[200px] bg-black rounded-xl border border-zinc-700 overflow-hidden relative">
             <div id="admin-map-container" className="w-full h-full z-0 cursor-crosshair"></div>
             {!selectedLocation && (
               <div className="absolute inset-0 z-10 bg-black/50 flex items-center justify-center pointer-events-none">
                 <p className="text-zinc-400 font-bold text-xs uppercase tracking-widest bg-black/80 px-4 py-2 rounded-full">Search or Click Map to Drop Pin</p>
               </div>
             )}
          </div>

          {/* Venue Details Form */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1 block">Venue Name</label>
              <input type="text" value={venueName} onChange={(e)=>setVenueName(e.target.value)} className="w-full bg-black border border-zinc-700 rounded-xl p-3 text-sm font-bold text-white" />
            </div>
            
            <div>
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1 block">Venue Type</label>
              <select value={venueType} onChange={(e)=>setVenueType(e.target.value)} className="w-full bg-black border border-zinc-700 rounded-xl p-3 text-sm font-bold text-white focus:outline-none">
                <option value="classic">Classic (Standard Pin)</option>
                <option value="partner">Partner (Beer Pin)</option>
                <option value="supply">Supply (Pill Pin)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1 block">Active Deal (Optional)</label>
              <input type="text" placeholder="e.g. BOGO Slice" value={venueDeal} onChange={(e)=>setVenueDeal(e.target.value)} className="w-full bg-black border border-zinc-700 rounded-xl p-3 text-sm font-bold text-white" />
            </div>
          </div>

          <button onClick={handleAddVenue} disabled={!selectedLocation || savingVenue} className="w-full bg-brand-teal text-black font-black py-4 rounded-xl flex items-center justify-center gap-2 uppercase tracking-widest shadow-lg mt-2 disabled:opacity-30 disabled:cursor-not-allowed">
            <Plus size={18} /> {savingVenue ? "Saving..." : "Save Venue to Map"}
          </button>
        </div>

        {/* MANAGE EXISTING VENUES */}
        <div className="bg-zinc-900 border border-white/10 p-6 rounded-2xl">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="text-brand-teal" size={20} />
            <h2 className="text-lg font-black uppercase tracking-tight">Active Map Venues</h2>
          </div>

          <div className="space-y-2">
            {venues.length === 0 ? (
              <p className="text-zinc-500 text-sm font-bold p-4 text-center border border-dashed border-zinc-700 rounded-xl">No venues added yet.</p>
            ) : (
              venues.map(v => (
                <div key={v.id} className="flex items-center justify-between p-3 border border-zinc-700 rounded-xl bg-black/50">
                  <div>
                    <p className="font-bold text-sm text-white flex items-center gap-2">
                      {v.name} 
                      <span className={`text-[8px] px-1.5 py-0.5 rounded font-black uppercase ${v.type === 'partner' ? 'bg-yellow-500/20 text-yellow-400' : v.type === 'supply' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-300'}`}>
                        {v.type}
                      </span>
                    </p>
                    {v.deal && <p className="text-[10px] font-bold text-brand-pink mt-0.5 uppercase">Deal: {v.deal}</p>}
                  </div>
                  <button onClick={() => handleDeleteVenue(v.id)} className="text-zinc-500 hover:text-red-500 p-2 transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
